/**
 * The answers that are "no", and how to say so.
 *
 * WHY THIS IS A MODULE AND NOT A STRING IN A CATCH BLOCK.
 *
 * A search box that takes plain English gets asked plain English, and most of English is not a
 * records question. People will type *whose team name is the worst*, *who's going to win this
 * year*, *why did Amit bench him*, and — because a box that looks like a chatbot gets treated like
 * one — things with nothing to do with fantasy football at all. Every one of those has to come
 * back with something, and the something matters more than the answers do: a manager who gets a
 * shrug the first time he tries this never comes back to it.
 *
 * So each way of missing gets its own reply, in the app's voice rather than the model's, saying
 * WHAT KIND of thing it cannot do and handing over two questions that work. Three properties are
 * worth keeping:
 *
 *   THE VOICE IS OURS. The model is asked only to name the kind. The words on screen are written
 *   here, so a refusal reads like the rest of the app rather than like a chat assistant
 *   apologising, and it says the same thing every time — which "I'm sorry, I can't help with that"
 *   never quite does.
 *
 *   THE OBVIOUS ONES NEVER LEAVE THE BROWSER. `classify` catches the common shapes before a paid
 *   call goes out. Free, instant, and it works in the standalone build, which has no server.
 *
 *   IT IS DELIBERATELY SHY. A wrongly refused real question is far worse than a subjective one
 *   that gets as far as the model, which will refuse it anyway. Every pattern below is narrow, and
 *   `worst manager` — a real question with a real answer since `pct` arrived — must never match.
 */

/** The ways a question misses. Ordered roughly by how often somebody will manage it. */
export type RefusalKind =
  /** A matter of taste. Nothing in a box score settles it. */
  | 'subjective'
  /** Real, recorded, and not in THIS archive — trades, keepers, FAAB, the draft. */
  | 'unrecorded'
  /** About a game that has not been played. */
  | 'future'
  /** Asks for a reason. The archive keeps what happened and never why. */
  | 'why'
  /** Not about this league at all. */
  | 'offtopic'
  /** Could not be read as a question about the record. */
  | 'unclear';

export interface RefusalCopy {
  /** The bold line. Says what kind of miss it was, in four or five words. */
  head: string;
  /** One sentence. Where the edge is, not an apology for it. */
  line: string;
  /** Two questions that do run. Both are canned, so both answer instantly and cost nothing. */
  suggest: [string, string];
  /**
   * `edge` — the question was outside what the archive holds. Nothing is broken and nobody did
   * anything wrong, so it should not be painted in the red this app uses for a real fault.
   * `fault` — something failed: the server, the parse, the query. That one earns the red.
   */
  tone?: 'edge' | 'fault';
}

/*
 * Every suggestion here is the exact text of a canned question, which is not a coincidence: the
 * screen matches them and runs the stored query rather than asking the model again. A dead end
 * that turns into an answer on one tap is barely a dead end.
 */
const WIN_PCT = 'Worst manager, by win percentage';
const BENCH = 'Most points ever left on a bench';
const MOST_WINS = 'Who has won the most games?';
const HIGH_WEEK = 'Highest single week ever';
const ZEROES = 'Most 0.0 scores on a winning starting lineup?';

const edge = (c: Omit<RefusalCopy, 'tone'>): RefusalCopy => ({ ...c, tone: 'edge' });

export const REFUSALS: Record<RefusalKind, RefusalCopy> = {
  subjective: edge({
    head: 'That one is a matter of taste.',
    line: 'The record keeps score, not opinions. It knows what every team was called; it has no '
      + 'column for which name was the worst.',
    suggest: [WIN_PCT, HIGH_WEEK],
  }),
  unrecorded: edge({
    head: 'Not in this archive.',
    line: 'This reads box scores — games, lineups and what each player scored. Trades, keepers, '
      + 'FAAB and the draft live on their own screens and it cannot see them.',
    suggest: [BENCH, MOST_WINS],
  }),
  future: edge({
    head: 'Nothing here predicts.',
    line: 'Every number on this screen comes from a game that has already been played.',
    suggest: [WIN_PCT, HIGH_WEEK],
  }),
  why: edge({
    head: 'It records what, never why.',
    line: 'Ask what happened and the box score is one tap under the answer. The reasoning is '
      + 'between you and whoever did it.',
    suggest: [BENCH, ZEROES],
  }),
  offtopic: edge({
    head: 'Wrong record.',
    line: 'This knows one thing: two decades of this league’s games, lineups and scores.',
    suggest: [MOST_WINS, HIGH_WEEK],
  }),
  unclear: edge({
    head: 'That did not come through as a question.',
    line: 'Name what you want counted — a manager, a week, a score, a lineup slot — and '
      + 'whether you want the most of it or the least.',
    suggest: [MOST_WINS, HIGH_WEEK],
  }),
};

/* -- catching the obvious ones before spending anything ---------------------------------------- */

/**
 * The patterns, in the order they are tried.
 *
 * NARROW ON PURPOSE. Each one had to survive the question it most resembles: `worst manager` is a
 * real query and must not read as subjective; `who has the best team` is a scoring question and
 * must not either. So taste is detected by the SUBJECT — a name, a logo, a personality — and never
 * by the superlative alone, which is the word every real records question is built on.
 */
const PATTERNS: { kind: RefusalKind; when: RegExp }[] = [
  // "who are you", "are you chatgpt" — a box that takes English gets talked to.
  { kind: 'offtopic', when: /\b(who|what) are you\b|\bare you (an?|the )?(ai|bot|robot|human|chatgpt|gemini|claude|real)\b|\bwhat('?s| is) your (name|favou?rite)\b/i },

  // A reason, not a fact. Cheap to spot and always right: nothing in a box score is a motive.
  { kind: 'why', when: /^\s*(why|how come)\b/i },

  // A game not yet played. "will win", "should I draft", "predict".
  { kind: 'future', when: /\b(will|would|gonna|going to)\s+(win|lose|make|miss|finish|score)\b|\b(predict|prediction|forecast|project(ed|ion)?s?|odds)\b|\bshould i\b|\bwho (do you )?(like|take|pick) (this|next) (year|season|week)\b/i },

  // Taste. The subject has to be one the archive holds no measure of.
  { kind: 'subjective', when: /\b(team ?names?|names?|logos?|avatars?|mascots?|trash ?talk(ing)?|banter|jerseys?)\b[^?]{0,40}\b(worst|best|funniest|dumbest|stupidest|coolest|greatest|lamest|favou?rite)\b|\b(worst|best|funniest|dumbest|stupidest|coolest|lamest|favou?rite)\b[^?]{0,40}\b(team ?names?|logos?|avatars?|mascots?|trash ?talk(ing)?|banter|jerseys?)\b/i },
  { kind: 'subjective', when: /\b(funniest|dumbest|stupidest|nicest|meanest|coolest|lamest|most annoying|best looking|worst dressed|smartest)\b/i },
  { kind: 'subjective', when: /\bwho (do|would) you\b|\bwhat do you (think|reckon)\b|\bin your opinion\b/i },

  // Real parts of the league that this archive has never held.
  { kind: 'unrecorded', when: /\b(trade[ds]?|trading|keepers?|faab|waivers?|auction|nomination|farm team|prospects?|rookie draft|draft(ed|ing)? (pick|round|order)|injur(y|ies|ed)|bye ?week)\b/i },
];

/**
 * What kind of miss this is, decided without a network call. `null` means "give it to the model".
 *
 * A question that gets past this is not thereby a good question — it is only one this cannot be
 * sure about, which is most of them and should be.
 */
export function classify(question: string): RefusalKind | null {
  const q = question.trim();
  // Nothing to work with: no word of three letters or more.
  if (!/[a-z]{3}/i.test(q)) return 'unclear';
  for (const p of PATTERNS) if (p.when.test(q)) return p.kind;
  return null;
}

/* -- what came back over the wire -------------------------------------------------------------- */

/**
 * The model saying it cannot express the question, and what to try instead.
 *
 * WORTH AS MUCH AS AN ANSWER, and treated as a first-class reply rather than a parse failure. The
 * `kind` is the useful part: given it, the screen prints OUR sentence for that kind rather than
 * whatever the model chose to write, so a refusal sounds like the app. `why` is kept only for the
 * case where no kind came back at all.
 *
 * Shapes accepted, because models are inconsistent about exactly this: a bare string, `{ message }`
 * or `{ why }` nested one deep, suggestions at either level.
 */
export interface Refusal { kind: RefusalKind | null; why: string; suggest: string[] }

const KINDS: RefusalKind[] = ['subjective', 'unrecorded', 'future', 'why', 'offtopic', 'unclear'];

export function parseRefusal(raw: unknown): Refusal | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;

  const found = o['error'] ?? o['cannot'] ?? o['unsupported'];
  if (found === undefined || found === null) return null;
  const nested = typeof found === 'object' ? found as Record<string, unknown> : null;

  const why = typeof found === 'string'
    ? found
    : typeof nested?.['message'] === 'string' ? nested['message'] as string
      : typeof nested?.['why'] === 'string' ? nested['why'] as string
        : typeof nested?.['reason'] === 'string' ? nested['reason'] as string
          : '';

  const kindRaw = String(o['kind'] ?? nested?.['kind'] ?? '').toLowerCase();
  const kind = KINDS.find((k) => k === kindRaw) ?? null;

  // A refusal with neither a kind nor a sentence is not a refusal, it is a stray key.
  if (!kind && !why) return null;

  const list = o['suggest'] ?? o['try'] ?? nested?.['suggest'] ?? nested?.['try'];
  const suggest = Array.isArray(list)
    ? list.filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
      .map((s) => s.trim()).slice(0, 3)
    : [];

  return { kind, why: why.trim(), suggest };
}

/** The house copy for a refusal, with the model's own sentence used only where we have none. */
export function refusalCopy(r: Refusal): RefusalCopy {
  if (r.kind) {
    const house = REFUSALS[r.kind];
    return r.suggest.length >= 2
      ? { ...house, suggest: [r.suggest[0]!, r.suggest[1]!] }
      : house;
  }
  return {
    head: 'That one has no answer here.',
    line: r.why || REFUSALS.unclear.line,
    suggest: r.suggest.length >= 2
      ? [r.suggest[0]!, r.suggest[1]!]
      : REFUSALS.unclear.suggest,
  };
}

/**
 * What the model is told about saying no. Sent with the schema, and mirrored in the function's
 * system prompt, because the browser's copy is the one somebody could edit.
 */
export function refusalPrompt(): string {
  return [
    'The archive is GAMES, LINEUPS AND SCORES and nothing else. It has never held a trade, a',
    'keeper, a FAAB bid, a draft pick, an injury or a bye week, and it cannot settle a matter of',
    'opinion, predict a game not yet played, or say why anybody did anything.',
    '',
    'If the question cannot be expressed as a query, do not guess. Answer instead with:',
    '',
    '{ "error": { "kind": "subjective" | "unrecorded" | "future" | "why" | "offtopic" | "unclear",',
    '             "why": "one short sentence" },',
    '  "suggest": ["a nearby question that IS answerable", "another"] }',
    '',
    '  subjective — a matter of taste: the worst team name, the funniest manager.',
    '  unrecorded — real, but not in a box score: trades, keepers, FAAB, the draft, injuries.',
    '  future     — about a game that has not been played, or advice on one.',
    '  why        — asks for a reason rather than a fact.',
    '  offtopic   — nothing to do with this league.',
    '  unclear    — you could not tell what was being asked.',
    '',
    'Getting the kind right matters more than the sentence: the app has its own words for each.',
    'A borderline question that CAN be counted should be counted — refuse only what cannot.',
  ].join('\n');
}
